/**
 * Timesheet-reminder definitions — the single source of truth shared by
 * the notifications fanouts (which schedule them) and the API (which lets
 * an admin fire one on demand). Keep `reminder → {schedule, period, cc}`
 * here so both sides agree.
 *
 * Business-confirmed cadence, applied to BOTH submission and approval
 * reminders. Weekly reminders every week, plus ONE month-end reminder on
 * the first working day of every month:
 *
 *   weekly_friday  — Friday 09:00,  current week, no CC (pre-deadline nudge).
 *   weekly_1       — Monday 09:00,  prior week,   no CC.
 *   weekly_2       — Tuesday 09:00, prior week,   CC supervisor + SR controller.
 *   monthly_first  — 1st working day 09:00, prior month, CC supervisor + SR controller.
 *
 * "supervisor" = the user's manager (`users.manager_id`); "SR controller"
 * = the functional head (`users.sr_controller_id`); "ops" = a fixed
 * configured distribution (env `OPS_REMINDER_EMAIL`, resolved in the
 * notifications service — see `resolveReminderCc`); no reminder uses it
 * today.
 *
 * "Working day" = a weekday (Mon–Fri); there is no holiday calendar. The
 * monthly reminder takes precedence: on the 1st working day no weekly
 * reminder goes out (enforced in the notifications `reminderFiresNow`).
 */

export type ReminderTrack = 'weekly' | 'monthly';

export type ReminderKey = 'weekly_friday' | 'weekly_1' | 'weekly_2' | 'monthly_first';

/** CC roles, escalating. `ops` resolves to the configured ops distribution. */
export type ReminderCcRole = 'manager' | 'head' | 'ops';

/** Which period a reminder evaluates: the in-progress one or the prior one. */
export type ReminderPeriod = 'current' | 'previous';

export type ReminderWeekday = 'Mon' | 'Tue' | 'Fri';
/** Ordinal working day of the month a monthly reminder fires on. */
export type ReminderWorkingDay = 'last' | 'first' | 'second';

/** When a reminder fires, in the recipient's local timezone. */
export type ReminderSchedule =
  | { kind: 'weekly'; weekday: ReminderWeekday; hour: number }
  | { kind: 'monthly'; workingDay: ReminderWorkingDay; hour: number };

export interface ReminderDef {
  /** Stable key — event-key prefix, log label, and API selector. */
  key: ReminderKey;
  templateName: string;
  schedule: ReminderSchedule;
  /** 'current' = the in-progress period; 'previous' = the prior one. */
  period: ReminderPeriod;
  /** Roles CC'd on the email, in order. */
  cc: ReminderCcRole[];
}

export const REMINDER_KEYS: readonly ReminderKey[] = [
  'weekly_friday',
  'weekly_1',
  'weekly_2',
  'monthly_first',
];

export const REMINDER_DEFS: ReminderDef[] = [
  {
    key: 'weekly_friday',
    templateName: 'timesheet.reminder_weekly_friday',
    schedule: { kind: 'weekly', weekday: 'Fri', hour: 9 },
    period: 'current',
    cc: [],
  },
  {
    key: 'weekly_1',
    templateName: 'timesheet.reminder_weekly_1',
    schedule: { kind: 'weekly', weekday: 'Mon', hour: 9 },
    period: 'previous',
    cc: [],
  },
  {
    key: 'weekly_2',
    templateName: 'timesheet.reminder_weekly_2',
    schedule: { kind: 'weekly', weekday: 'Tue', hour: 9 },
    period: 'previous',
    cc: ['manager', 'head'],
  },
  {
    key: 'monthly_first',
    templateName: 'timesheet.reminder_monthly_first',
    schedule: { kind: 'monthly', workingDay: 'first', hour: 9 },
    period: 'previous',
    cc: ['manager', 'head'],
  },
];

/**
 * The approver-reminder digest template name. It lives outside REMINDER_DEFS
 * because approvers share ONE template across the whole cadence (the email
 * body is identical regardless of which definition fired); the definitions
 * only drive WHEN it sends and WHO is CC'd.
 */
export const APPROVER_REMINDER_TEMPLATE_NAME = 'timesheet.approver_digest';

/**
 * Every reminder template name — the submission reminders (REMINDER_DEFS)
 * plus the approver digest. Single source of truth so an "is this a
 * reminder template?" check (e.g. the sandbox email override) cannot miss
 * one and leak to real recipients — the exact failure the approver digest
 * once had. Add a new reminder template here and to its definition.
 */
export const REMINDER_TEMPLATE_NAMES: readonly string[] = [
  ...REMINDER_DEFS.map((d) => d.templateName),
  APPROVER_REMINDER_TEMPLATE_NAME,
];

/** Look up a reminder definition by key (undefined for an unknown key). */
export function reminderDef(key: string): ReminderDef | undefined {
  return REMINDER_DEFS.find((d) => d.key === key);
}

/** Look up a reminder definition by its template name (undefined if none). */
export function reminderDefByTemplate(templateName: string): ReminderDef | undefined {
  return REMINDER_DEFS.find((d) => d.templateName === templateName);
}

/**
 * Resolve a reminder's CC addresses from its role list and the recipient's
 * resolved supervisor / SR-controller emails. Skips roles with no address
 * on file, drops the recipient's own address (a Sr. Controller can now be
 * their own `functionalHeadEmail` — see users.sql — and a manager could in
 * principle resolve the same way), and de-duplicates (supervisor and SR
 * controller can be the same OTHER person). Single source for both the cron
 * fanout and the manual admin trigger.
 *
 * The `ops` role resolves to a configured distribution that lives in the
 * notifications service env, not here — it is appended in exactly one place
 * (the notifications `appendOpsCc`) so the cron and manual paths can't
 * drift. This function therefore ignores the `ops` role.
 */
export function resolveReminderCc(
  roles: readonly ReminderCcRole[],
  emails: { managerEmail: string | null; functionalHeadEmail: string | null },
  recipientEmail: string,
): string[] {
  const set = new Set<string>();
  for (const role of roles) {
    if (role === 'manager') {
      if (emails.managerEmail) set.add(emails.managerEmail);
    } else if (role === 'head') {
      if (emails.functionalHeadEmail) set.add(emails.functionalHeadEmail);
    }
    // 'ops' is appended downstream (notifications appendOpsCc).
  }
  // Case-insensitive: every current write path lowercases `users.email`
  // before storing it, but that's an application-level convention, not a
  // DB-level guarantee (the column is plain `text`, not `citext`) — don't
  // let a differently-cased row silently defeat the self-CC exclusion.
  const recipient = recipientEmail.toLowerCase();
  for (const addr of set) {
    if (addr.toLowerCase() === recipient) set.delete(addr);
  }
  return [...set];
}

// ---- week math (UTC) ------------------------------------------------------

/** Monday (UTC) of the current week, as YYYY-MM-DD. */
export function currentMondayIso(now: Date = new Date()): string {
  const dow = now.getUTCDay();
  const daysSinceMonday = (dow + 6) % 7;
  const d = new Date(now);
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  return d.toISOString().slice(0, 10);
}

/** Monday (UTC) of the previous week, as YYYY-MM-DD. */
export function lastMondayIso(now: Date = new Date()): string {
  const dow = now.getUTCDay();
  const daysSinceLastMonday = ((dow + 6) % 7) + 7;
  const d = new Date(now);
  d.setUTCDate(d.getUTCDate() - daysSinceLastMonday);
  return d.toISOString().slice(0, 10);
}

/** Add `days` to a YYYY-MM-DD date (UTC), returning YYYY-MM-DD. */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// ---- month math (UTC) -----------------------------------------------------

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** First day (YYYY-MM-01) of the current month. */
export function monthStartIso(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}-01`;
}

/** First day (YYYY-MM-01) of the previous month. */
export function previousMonthStartIso(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-01`;
}

/** Number of days in human month `month` (1–12) of `year`. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Last calendar day (YYYY-MM-DD) of the month that `monthStartIso` begins. */
export function monthEndIso(monthStart: string): string {
  const y = Number(monthStart.slice(0, 4));
  const m = Number(monthStart.slice(5, 7));
  return `${y}-${pad2(m)}-${pad2(daysInMonth(y, m))}`;
}

/** 0=Sun … 6=Sat for the calendar date (year, human-month 1–12, day). */
function weekdayOfDate(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** True iff the date falls Mon–Fri. */
function isWeekday(year: number, month: number, day: number): boolean {
  const wd = weekdayOfDate(year, month, day);
  return wd >= 1 && wd <= 5;
}

/** Day-of-month (1-based) of the first weekday of human month `month`. */
export function firstWorkingDayOfMonth(year: number, month: number): number {
  for (let d = 1; d <= 7; d++) if (isWeekday(year, month, d)) return d;
  return 1; // unreachable — any 7-day window contains a weekday
}

/** Day-of-month (1-based) of the second weekday of human month `month`. */
export function secondWorkingDayOfMonth(year: number, month: number): number {
  let seen = 0;
  for (let d = 1; d <= 10; d++) {
    if (isWeekday(year, month, d)) {
      seen++;
      if (seen === 2) return d;
    }
  }
  return 2; // unreachable
}

/** Day-of-month (1-based) of the last weekday of human month `month`. */
export function lastWorkingDayOfMonth(year: number, month: number): number {
  for (let d = daysInMonth(year, month); d >= 1; d--) if (isWeekday(year, month, d)) return d;
  return daysInMonth(year, month); // unreachable
}

/** Day-of-month (1-based) of the first / second / last weekday of `month`. */
export function getWorkingDayOfMonth(
  year: number,
  month: number,
  position: ReminderWorkingDay,
): number {
  return position === 'last'
    ? lastWorkingDayOfMonth(year, month)
    : position === 'first'
      ? firstWorkingDayOfMonth(year, month)
      : secondWorkingDayOfMonth(year, month);
}

/** Hours in a standard working day — the unit behind the monthly-capacity
 *  fallback when an employee has no recorded `hours_per_month`. */
export const WORKDAY_HOURS = 8;

/**
 * Default monthly capacity (hours) for a period that has no recorded
 * `hours_per_month`: the period's working days (Mon–Fri) × `WORKDAY_HOURS`.
 * Single source for the "× 8" rule so future changes (part-time scaling,
 * holidays) land in one place.
 */
export function monthlyCapacityHours(periodStartIso: string, periodEndIso: string): number {
  return workingDaysInRange(periodStartIso, periodEndIso) * WORKDAY_HOURS;
}

/** True when `iso` (YYYY-MM-DD) is a working day: Mon–Fri. There is no
 *  holiday calendar; this is the one place the rule lives. */
export function isWorkingDay(iso: string): boolean {
  const wd = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return wd >= 1 && wd <= 5;
}

/** Count of weekdays (Mon–Fri) in the inclusive [startIso, endIso] range. */
export function workingDaysInRange(startIso: string, endIso: string): number {
  let count = 0;
  for (let d = startIso; d <= endIso; d = addDaysIso(d, 1)) {
    if (isWorkingDay(d)) count++;
  }
  return count;
}

// ---- period resolution ----------------------------------------------------

/**
 * The [start, end] period a reminder targets and the defaulter basis to
 * evaluate it against. Weekly reminders target a Mon–Sun week; monthly
 * reminders target a calendar month.
 */
export function reminderPeriodRange(
  def: ReminderDef,
  now: Date = new Date(),
): { periodStart: string; periodEnd: string; basis: ReminderTrack } {
  if (def.schedule.kind === 'weekly') {
    const periodStart = def.period === 'current' ? currentMondayIso(now) : lastMondayIso(now);
    return { periodStart, periodEnd: addDaysIso(periodStart, 6), basis: 'weekly' };
  }
  const periodStart = def.period === 'current' ? monthStartIso(now) : previousMonthStartIso(now);
  return { periodStart, periodEnd: monthEndIso(periodStart), basis: 'monthly' };
}
